import { useEffect, useMemo } from "react";
import { useProject } from "../../../hooks/query";

export interface DashboardStats {
    running: number;
    completed: number;
    atRisk: number;
    total: number;
}

export interface DeadlineStats {
    beforeDeadline: number;
    onDeadline: number;
    afterDeadline: number;
    total: number;
}

export function useDashboardData() {
    const { useList } = useProject();
    const listSvc = useList();
    const projects = listSvc.data || [];

    useEffect(() => {
        listSvc.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const stats = useMemo(() => {
        const running = projects.filter((p: any) => p.Status === "IN-PROGRESS" || p.Status === "active").length;
        const completed = projects.filter((p: any) => p.Status === "COMPLETED").length;
        const atRisk = projects.filter((p: any) => {
            const now = new Date();
            const estimatedEnd = p.EstimatedEndDate ? new Date(p.EstimatedEndDate) : null;
            return p.Status !== "COMPLETED" && estimatedEnd && now > estimatedEnd;
        }).length;

        return {
            running,
            completed,
            atRisk,
            total: projects.length
        };
    }, [projects]);

    const deadlineStats = useMemo(() => {
        const now = new Date();
        const currentMonth = now.getMonth();
        const currentYear = now.getFullYear();
        const prevMonth = currentMonth === 0 ? 11 : currentMonth - 1;
        const prevYear = currentMonth === 0 ? currentYear - 1 : currentYear;

        let beforeDeadline = 0;
        let onDeadline = 0;
        let afterDeadline = 0;
        let prevBeforeDeadline = 0;
        let prevOnDeadline = 0;
        let prevAfterDeadline = 0;

        const categorize = (p: any) => {
            const estimatedEnd = p.EstimatedEndDate ? new Date(p.EstimatedEndDate) : null;
            const completionDate = p.CompletionDate || p.ActualEndDate;
            const actualEnd = completionDate ? new Date(completionDate) : null;
            const totalTasks = p.TaskSummary?.TotalTasks || 0;
            const completedTasks = p.TaskSummary?.COMPLETED || 0;
            const isCompleted = p.Status === "COMPLETED" || completedTasks === totalTasks;

            if (!estimatedEnd || totalTasks === 0) return null;

            if (isCompleted && actualEnd) {
                const diffDays = Math.round((actualEnd.getTime() - estimatedEnd.getTime()) / (1000 * 60 * 60 * 24));
                if (diffDays < 0) return "before";
                if (diffDays <= 1) return "on";
                return "after";
            } else if (!isCompleted) {
                if (now > estimatedEnd) return "after";
                return "on";
            }
            return null;
        };

        projects.forEach((p: any) => {
            const startDate = p.StartDate ? new Date(p.StartDate) : null;
            const category = categorize(p);
            if (!category || !startDate) return;

            const pMonth = startDate.getMonth();
            const pYear = startDate.getFullYear();
            const isCurrent = pMonth === currentMonth && pYear === currentYear;
            const isPrev = pMonth === prevMonth && pYear === prevYear;

            if (isCurrent || (!isCurrent && !isPrev)) {
                // Current month or projects without a clear month go to current
                if (category === "before") beforeDeadline++;
                else if (category === "on") onDeadline++;
                else afterDeadline++;
            }
            if (isPrev) {
                if (category === "before") prevBeforeDeadline++;
                else if (category === "on") prevOnDeadline++;
                else prevAfterDeadline++;
            }
        });

        const calcTrend = (current: number, prev: number) => {
            if (prev === 0) return current > 0 ? "+100%" : "0%";
            const change = Math.round(((current - prev) / prev) * 100);
            return change > 0 ? `+${change}%` : `${change}%`;
        };

        return {
            beforeDeadline,
            onDeadline,
            afterDeadline,
            prevBeforeDeadline,
            prevOnDeadline,
            prevAfterDeadline,
            beforeTrend: calcTrend(beforeDeadline, prevBeforeDeadline),
            onTrend: calcTrend(onDeadline, prevOnDeadline),
            afterTrend: calcTrend(afterDeadline, prevAfterDeadline),
            total: beforeDeadline + onDeadline + afterDeadline,
        };
    }, [projects]);

    const leadDistribution = useMemo(() => {
        const sourceMap: Record<string, number> = {};
        let totalWithLead = 0;

        projects.forEach((p: any) => {
            const sourceName = p.Lead?.LeadSource?.Name || "Unknown";
            sourceMap[sourceName] = (sourceMap[sourceName] || 0) + 1;
            totalWithLead++;
        });

        const items = Object.entries(sourceMap)
            .map(([name, count]) => ({
                name,
                count,
                percentage: totalWithLead > 0 ? Math.round((count / totalWithLead) * 100) : 0,
            }))
            .sort((a, b) => b.count - a.count);

        return { items, total: totalWithLead };
    }, [projects]);

    const monthlyProjectStats = useMemo(() => {
        const now = new Date();
        const months: string[] = [];
        const created: number[] = [];
        const completed: number[] = [];
        const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

        // Build last 6 months (oldest to newest)
        for (let i = 5; i >= 0; i--) {
            const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
            months.push(`${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`);
            created.push(0);
            completed.push(0);
        }

        projects.forEach((p: any) => {
            // Count created by StartDate
            if (p.StartDate) {
                const sd = new Date(p.StartDate);
                const label = `${MONTH_NAMES[sd.getMonth()]} ${sd.getFullYear()}`;
                const idx = months.indexOf(label);
                if (idx !== -1) created[idx]++;
            }

            // Count completed by ActualEndDate or CompletionDate
            const completionDate = p.CompletionDate || p.ActualEndDate;
            if (completionDate && p.Status === "COMPLETED") {
                const cd = new Date(completionDate);
                const label = `${MONTH_NAMES[cd.getMonth()]} ${cd.getFullYear()}`;
                const idx = months.indexOf(label);
                if (idx !== -1) completed[idx]++;
            }
        });

        return { months, created, completed };
    }, [projects]);

    return {
        projects,
        stats,
        deadlineStats,
        leadDistribution,
        monthlyProjectStats,
        isLoading: listSvc.isPending,
        refresh: () => listSvc.mutate()
    };
}
